/**
 * THE TRANSPORT BOUNDARY — the one place this frontend performs an HTTP request.
 *
 * ============================================================================
 * NOTHING IN THIS FILE RUNS IN THE CURRENT BUILD, AND THAT IS NOT A CONTRADICTION.
 *
 * Every module under src/api/ is a mock (see api/README.md), so no call site reaches
 * `request()` today: `USE_MOCK_API` is true, `apiUrl()` throws by design when no base
 * URL is configured, and each mock body returns a fixture instead. What this file does
 * is fix the SHAPE of the eventual request so that turning a mock into a real call is
 * deleting a fixture and awaiting `request()` — not designing a transport per module.
 *
 * The audit's finding was precise: the API boundary existed but "no real HTTP request
 * helper currently attaches authentication", 401 handling was nowhere, and `apiUrl()`
 * had no caller. This is that helper, and it is deliberately the ONLY one. A second
 * request helper is the duplicate architecture the brief rules out — if a module needs
 * something this does not do, widen `request()` rather than writing a sibling.
 * ============================================================================
 *
 * WHAT LIVES HERE, AND WHY EACH IS HERE RATHER THAN AT A CALL SITE
 * ---------------------------------------------------------------
 *   base URL join      via config.apiUrl() — host knowledge stays in config.ts
 *   Authorization      one attachment point, one token holder, no component reads it
 *   request headers    Accept/Content-Type set once, correctly, including no
 *                      Content-Type on a bodyless request
 *   success parsing    204 and empty bodies handled, so `Promise<void>` endpoints work
 *   error normalising  the §2 error envelope becomes an ApiError via api/errors.ts
 *   401 handling       one hook, fired once per unauthorised response
 *
 * WHAT IS DELIBERATELY NOT HERE. No toast, no navigation, no retry, no store access —
 * the same boundary api/errors.ts draws and for the same reason. This file cannot
 * import a store: api/README.md's rule is that only plain domain objects cross the
 * boundary ("No React, no store access, no component types"), and a transport that
 * reached into authStore would make the API layer depend on the state layer it serves.
 * The token arrives by injection (`setAuthToken`) and a 401 leaves by callback
 * (`setUnauthorizedHandler`). Who calls those is the app's business, not this file's.
 *
 * See docs/api-contract.md §2 for the wire format, and §21 open question 1 for the one
 * transport decision this file does NOT make on the project's behalf — read on.
 */
import { apiUrl } from '../config';
import { ApiError, toApiError } from './errors';

/* ===========================================================================
   THE TRANSPORT MECHANISM — WHAT THE CONTRACT SETTLES AND WHAT IT DOES NOT
   ===========================================================================
   BEARER, BECAUSE THE CONTRACT ALREADY SAYS SO. docs/api-contract.md §2 states the
   convention outright — `Authorization: Bearer <token>` — and `AuthSession.token`
   exists in types/domain.ts for it, currently always null "deliberately, so no code
   starts treating a fake token as real". So attaching a bearer header is implementing
   a documented decision, not inventing one.

   WHAT IS GENUINELY UNRESOLVED, and is therefore NOT decided here: §21 open question 1
   asks whether the backend will prefer a cookie session over a bearer token, and "Is
   there a refresh token, and what should the frontend do on a 401 mid-session?" Neither
   has an answer yet, and this file does not manufacture one:

     - `credentials` is configurable per request and defaults to 'same-origin', which
       is fetch's own default. A cookie-session backend becomes `credentials: 'include'`
       at this one call site plus a contract edit — it does not become a rewrite,
       because no module below this one knows how auth travels.
     - THERE IS NO SILENT TOKEN REFRESH, and there must not be one invented: no refresh
       token is issued anywhere in this app, and `authApi.refreshSession()` throws 501
       precisely so nothing pretends otherwise. A refresh-and-retry loop written against
       an endpoint that does not exist would be fake authentication. A 401 therefore
       clears the token and fires the handler, once. When the backend defines refresh,
       the retry belongs here — in this file, in one place.
*/

/** fetch's credentials mode. Named so the cookie-vs-bearer switch has a type. */
export type RequestCredentials_ = 'omit' | 'same-origin' | 'include';

/**
 * The token attached to every authenticated request, or null when nobody is signed in.
 *
 * MODULE-SCOPED AND NOT EXPORTED, so there is exactly one answer to "what token are we
 * sending" and no component can read or set it directly — which is the brief's "do not
 * hardcode credentials" and "one centralized authentication attachment mechanism" as
 * code rather than as a convention. It is deliberately NOT in localStorage: where a
 * token may be persisted is a security decision that depends on the mechanism §21 has
 * not settled, and writing one to disk now would prejudge it.
 */
let authToken: string | null = null;

/**
 * Sets (or clears) the token every subsequent request carries.
 *
 * Called by the auth store when a session begins, is restored, or ends — one writer,
 * so the transport and the store cannot disagree about who is signed in.
 */
export function setAuthToken(token: string | null): void {
  authToken = token;
}

/** The current token. Exported for assertions and diagnostics, not for request code. */
export function getAuthToken(): string | null {
  return authToken;
}

/** What runs when the server says the session is gone. */
export type UnauthorizedHandler = () => void;

/**
 * Notified on any real auth/session failure, after the token has been cleared.
 *
 * ONE HOOK, NOT A LISTENER LIST: "centralized 401 handling" means a single answer to
 * "the session expired" — sign the user out and let the router's gate send them to
 * /login. Several subscribers would each decide separately and race.
 */
let onUnauthorized: UnauthorizedHandler | null = null;

/** Registers the app's response to an expired session. Pass null to detach. */
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  onUnauthorized = handler;
}

/** The HTTP verbs this API uses. Spelled out so a typo is a compile error. */
export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export interface RequestOptions {
  /** Defaults to GET. */
  method?: HttpMethod;
  /** Serialised as JSON. Omit for a bodyless request — see the Content-Type note. */
  body?: unknown;
  /** Query parameters. Undefined and null values are dropped rather than sent as "undefined". */
  query?: Record<string, string | number | boolean | undefined | null>;
  /**
   * Additional headers for this request.
   *
   * Used sparingly — for example, an idempotency key on generation job creation — so the
   * one transport still owns how every request is built.
   */
  headers?: Record<string, string>;
  /**
   * Send the Authorization header. Default true.
   *
   * `false` is for the genuinely public endpoints — the contract marks the shared-song
   * lookup and licence verification "PUBLIC — no auth" — where sending a token would
   * make a public read look like an authenticated one in the server's logs.
   */
  auth?: boolean;
  /** Cookie mode. See the transport-mechanism note above. */
  credentials?: RequestCredentials_;
  /** Aborts the request. Passed through to fetch. */
  signal?: AbortSignal;
}

/**
 * The error envelope docs/api-contract.md §2 fixes:
 *   { "error": { "code": "insufficient_credits", "message": "…" } }
 */
interface ErrorEnvelope {
  error?: { code?: string; message?: string };
}

/** True when a parsed body looks like the §2 error envelope. */
function isErrorEnvelope(value: unknown): value is ErrorEnvelope {
  return typeof value === 'object' && value !== null && 'error' in value;
}

/**
 * True for a body fetch already knows how to send as-is — a Blob, an ArrayBuffer, a
 * typed array, or a FormData — as opposed to a plain object that needs `JSON.stringify`.
 *
 * WHY THIS EXISTS RATHER THAN A SECOND `request`-LIKE FUNCTION: the file's own rule is
 * "widen `request()` rather than writing a sibling", and the only two things that change
 * for a binary body are (1) do not stringify it and (2) do not guess its Content-Type —
 * the caller already knows it (a file's real MIME type, or FormData's own boundary
 * header) and stating one here would be inventing a fact this file cannot know.
 */
function isBinaryBody(value: unknown): value is BodyInit {
  return (
    value instanceof Blob ||
    value instanceof ArrayBuffer ||
    ArrayBuffer.isView(value) ||
    value instanceof FormData
  );
}

/** Appends query parameters, dropping the ones with nothing to say. */
function withQuery(url: string, query: RequestOptions['query']): string {
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}${url.includes('?') ? '&' : '?'}${qs}` : url;
}

/**
 * Performs one API request and returns its parsed body.
 *
 * THROWS AN `ApiError`, ALWAYS — never a raw TypeError, never a bare string, never a
 * rejected promise carrying a Response. Every consumer in this app already knows how to
 * read an ApiError (`kind`, `apiErrorMessage`, `failedLoad`), so a store wrapping this
 * needs no transport-specific branch. That is the whole point of normalising here:
 * `toApiError` maps a failed fetch onto status 0 / kind 'network' and everything else
 * onto the §2 status table, in one place, tested once.
 *
 * `T` is unchecked at runtime. A JSON body is `unknown` and this does not validate it —
 * pretending otherwise would be a false guarantee. The mocks return typed fixtures and
 * the eventual backend is the contract's responsibility; if runtime validation is wanted
 * later it belongs at this boundary, which is why every response passes through here.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const {
    method = 'GET',
    body,
    query,
    headers: extraHeaders,
    auth = true,
    credentials = 'same-origin',
    signal,
  } = options;

  // Throws when no backend is configured — deliberately, and see config.apiUrl(): a
  // thrown error here means real transport was reached while the app is in mock mode,
  // which is a wiring bug worth failing loudly on rather than a request to a relative
  // path that 404s against the dev server's index.html.
  const url = withQuery(apiUrl(path), query);

  const headers: Record<string, string> = { Accept: 'application/json', ...(extraHeaders ?? {}) };

  // Only when there IS a body. A Content-Type on a bodyless GET is not merely noise:
  // it makes the request non-simple for CORS, so a preflight is required where none
  // would otherwise be — a real failure mode, from a header that described nothing.
  //
  // A binary body is exempt: the caller either already knows the real Content-Type (a
  // file's own MIME type) or is sending FormData, whose boundary header fetch sets
  // itself and which this line would otherwise clobber with a wrong 'application/json'.
  if (body !== undefined && !isBinaryBody(body)) headers['Content-Type'] = 'application/json';

  // THE ONE PLACE A CREDENTIAL IS ATTACHED. Not in a component, not in a store, not
  // per module. Absent when there is no token, rather than sent as "Bearer null".
  if (auth && authToken) headers.Authorization = `Bearer ${authToken}`;

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers,
      credentials,
      signal,
      ...(body === undefined ? {} : { body: isBinaryBody(body) ? body : JSON.stringify(body) }),
    });
  } catch {
    // The request never reached a server: offline, DNS, CORS, or an abort. This branch is
    // the one place that knows that for certain, so the transport classifies it explicitly
    // instead of asking the generic error normaliser to infer it from a constructor name.
    throw new ApiError(0, 'Could not reach the server. Check your connection.', 'network_error');
  }

  /*
    THE BODY IS READ AS TEXT FIRST, THEN PARSED — deliberately, because `response.json()`
    on an empty body throws a SyntaxError, and a 204 No Content is the correct answer to
    a DELETE. Reading text lets "no body" resolve to undefined instead of failing a
    request that actually succeeded.
  */
  const raw = await response.text().catch(() => '');
  let parsed: unknown;
  if (raw) {
    try {
      parsed = JSON.parse(raw);
    } catch {
      /*
        A non-JSON body. On a failure this is the usual shape of a proxy or gateway
        error page, and the HTML is not a message to show anyone — the status decides
        the message instead. On a success it is a contract violation, reported as one.
      */
      if (response.ok) {
        throw new ApiError(response.status, 'The server sent a response we could not read.');
      }
      parsed = undefined;
    }
  }

  if (!response.ok) {
    const envelope = isErrorEnvelope(parsed) ? parsed.error : undefined;
    const code = envelope?.code;

    /*
      THE MESSAGE COMES FROM THE SERVER WHEN IT SENT ONE. §2 is explicit that `message`
      is "written for a human" and shown in a toast verbatim, so replacing it with a
      generic sentence would discard the one string authored by whoever knew what went
      wrong. When there is no envelope, ApiError's own status-derived text is used.
    */
    const error = new ApiError(
      response.status,
      envelope?.message || `Request failed (${response.status}).`,
      code
    );

    // CLEARED BEFORE THE HANDLER RUNS, so nothing can fire a second request carrying
    // the token the server just rejected. Only a real auth/session failure should reach
    // this branch — a 403 means the user is signed in but not allowed to do that.
    if (error.kind === 'auth') {
      authToken = null;
      onUnauthorized?.();
    }

    throw error;
  }

  // `undefined as T` is the honest value for a 204: there was no body, and inventing
  // `{}` would hand a caller an object the server never sent. Endpoints that return
  // nothing are typed `Promise<void>`, for which this is correct.
  return parsed as T;
}

export interface BlobResponse {
  blob: Blob;
  mimeType: string;
}

/**
 * Performs one API request whose success body is binary rather than JSON.
 *
 * This keeps the same auth attachment and error normalisation as `request()`. The one
 * thing that changes is the success path: preview audio is bytes, not a JSON document.
 */
export async function requestBlob(path: string, options: RequestOptions = {}): Promise<BlobResponse> {
  const {
    method = 'GET',
    body,
    query,
    headers: extraHeaders,
    auth = true,
    credentials = 'same-origin',
    signal,
  } = options;

  const url = withQuery(apiUrl(path), query);
  const headers: Record<string, string> = { Accept: '*/*', ...(extraHeaders ?? {}) };

  if (body !== undefined && !isBinaryBody(body)) headers['Content-Type'] = 'application/json';
  if (auth && authToken) headers.Authorization = `Bearer ${authToken}`;

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers,
      credentials,
      signal,
      ...(body === undefined ? {} : { body: isBinaryBody(body) ? body : JSON.stringify(body) }),
    });
  } catch (err) {
    throw toApiError(err, { preserveMessage: false });
  }

  if (!response.ok) {
    const raw = await response.text().catch(() => '');
    let parsed: unknown;
    if (raw) {
      try {
        parsed = JSON.parse(raw);
      } catch {
        parsed = undefined;
      }
    }

    const envelope = isErrorEnvelope(parsed) ? parsed.error : undefined;
    const error = new ApiError(
      response.status,
      envelope?.message || `Request failed (${response.status}).`,
      envelope?.code
    );

    if (error.kind === 'auth') {
      authToken = null;
      onUnauthorized?.();
    }

    throw error;
  }

  return {
    blob: await response.blob(),
    mimeType: response.headers.get('content-type') ?? 'application/octet-stream',
  };
}

/**
 * Convenience verbs. Thin on purpose — they add nothing but a method name, so there is
 * still one implementation and one place a header or an error rule can be got wrong.
 */
export const http = {
  request,
  requestBlob,

  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> =>
    request<T>(path, { ...options, method: 'GET' }),

  getBlob: (path: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<BlobResponse> =>
    requestBlob(path, { ...options, method: 'GET' }),

  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> =>
    request<T>(path, { ...options, method: 'POST', body }),

  postBlob: (path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<BlobResponse> =>
    requestBlob(path, { ...options, method: 'POST', body }),

  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> =>
    request<T>(path, { ...options, method: 'PATCH', body }),

  delete: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> =>
    request<T>(path, { ...options, method: 'DELETE', body }),
};
